// @ts-nocheck
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@saas-boilerplate/shared';
import { useWorkspace } from '@/contexts/context';
import { useAuth } from '@/contexts/auth-context';
import { AppTopBar } from '@/components/app-top-bar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Search, X, ChevronDown, Check, UserCircle, CalendarDays, Plus, Pencil, Trash2, MessageSquare, Sparkles } from 'lucide-react';
import { ActionItemThread } from '@/components/action-item-thread';
import { ActionItemCleaner } from '@/components/action-item-cleaner';
import { track } from '@/lib/analytics';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/components/ui/utils';

const ACTION_ITEM_STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  // Set by the cleaner's archive swipe. Kept out of every open-item surface (nudges, recaps,
  // AI carryover) but still selectable here so an item can be brought back.
  { value: 'dropped', label: 'Archived' },
] as const;

type ActionStatus = 'open' | 'in_progress' | 'done' | 'dropped';

const DEFAULT_STATUS_FILTER: ReadonlyArray<ActionStatus> = ['open', 'in_progress'];

// Age-bucket drill-down from the dashboard "Open actions by age" chart (DR-13).
const AGE_BUCKET_LABELS: Record<string, string> = {
  '0-1': '0–1 days old',
  '2-7': '2–7 days old',
  '8-14': '8–14 days old',
  '15-30': '15–30 days old',
  '30+': '30+ days old',
};
function actionAgeInBucket(createdAt: string | null, bucket: string): boolean {
  if (!createdAt) return false;
  const t = new Date(createdAt).getTime();
  if (!Number.isFinite(t)) return false;
  const days = (Date.now() - t) / 86_400_000;
  switch (bucket) {
    case '0-1': return days <= 1;
    case '2-7': return days > 1 && days <= 7;
    case '8-14': return days > 7 && days <= 14;
    case '15-30': return days > 14 && days <= 30;
    case '30+': return days > 30;
    default: return true;
  }
}

type Row = {
  id: string;
  title: string;
  status: string;
  updated_at: string;
  created_at: string | null;
  meeting_id: string;
  assignee_name: string | null;
  assignee_email: string | null;
  assignee_user_id: string | null;
  meetings: {
    id: string;
    starts_at: string | null;
    meeting_series: { id: string; title: string | null } | null;
  };
};

type MemberOption = {
  user_id: string;
  full_name: string | null;
  email: string | null;
};

type MeetingOption = {
  id: string;
  starts_at: string | null;
  series_title: string | null;
};

function assigneeDisplay(r: Row): string {
  const n = r.assignee_name?.trim();
  if (n) return n;
  const em = r.assignee_email?.trim();
  if (em) return em;
  if (r.assignee_user_id) return 'Assigned (member)';
  return '';
}

function formatSessionDate(startsAt: string | null): string {
  if (!startsAt) return 'Session';
  const d = new Date(startsAt);
  if (Number.isNaN(d.getTime())) return 'Session';
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'done':
      return 'bg-emerald-100 text-emerald-900 border-emerald-200';
    case 'in_progress':
      return 'bg-amber-100 text-amber-900 border-amber-200';
    case 'dropped':
      return 'bg-orange-100 text-orange-900 border-orange-200';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

function normalizeActionStatus(status: string | undefined): ActionStatus {
  if (status === 'done' || status === 'in_progress' || status === 'open' || status === 'dropped') {
    return status;
  }
  return 'open';
}

const ASSIGNEE_UNASSIGNED = '__unassigned__';

export function ActionItemsPage({
  userProfile,
  onSignOut,
  onSwitchWorkspace,
  onHelp,
  initialAgeBucket = null,
  openCleanerOnMount = false,
  onConsumedOpenCleaner,
}: {
  userProfile?: { full_name?: string; email?: string; role?: string } | null;
  onSignOut?: () => void;
  onSwitchWorkspace?: () => void;
  onHelp?: () => void;
  /** Pre-applied age filter from the dashboard "Open actions by age" drill-down (DR-13). */
  initialAgeBucket?: string | null;
  /** Set when the backlog banner sent the user here to run the cleaner. */
  openCleanerOnMount?: boolean;
  onConsumedOpenCleaner?: () => void;
}) {
  const { workspace } = useWorkspace();
  const { user } = useAuth();
  const workspaceId = workspace?.id;
  const currentUserId = user?.id || null;

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Row[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  // ── Filters ──
  const [assignedToMe, setAssignedToMe] = useState(Boolean(initialAgeBucket));
  // Age filter applied when arriving from the dashboard "Open actions by age" chart (DR-13).
  const [ageBucket, setAgeBucket] = useState<string | null>(initialAgeBucket ?? null);
  const [titleQuery, setTitleQuery] = useState('');
  /** Empty string = no assignee filter. Otherwise either a workspace_member user_id, or "ext:<lowered name|email>"
   *  for legacy/external assignees, or "__unassigned__". */
  const [assigneeFilter, setAssigneeFilter] = useState<string>('');
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);
  /** Empty string = no meeting filter; otherwise a meeting_series id. */
  const [meetingFilter, setMeetingFilter] = useState<string>('');
  const [meetingPickerOpen, setMeetingPickerOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<Set<ActionStatus>>(
    new Set(DEFAULT_STATUS_FILTER),
  );

  // ── Swipe cleaner ──
  const [cleanerOpen, setCleanerOpen] = useState(false);

  // ── Inline title edit ──
  const [titleEditId, setTitleEditId] = useState<string | null>(null);
  const [titleEditText, setTitleEditText] = useState('');

  // ── Per-item updates thread (comments + status timeline) ──
  const [threadOpenId, setThreadOpenId] = useState<string | null>(null);

  // ── Add dialog ──
  const [addOpen, setAddOpen] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addMeetingId, setAddMeetingId] = useState<string>('');
  const [addAssigneeUserId, setAddAssigneeUserId] = useState<string>(ASSIGNEE_UNASSIGNED);
  const [addMeetingPickerOpen, setAddMeetingPickerOpen] = useState(false);
  const [addMeetingSearch, setAddMeetingSearch] = useState('');
  const [meetings, setMeetings] = useState<MeetingOption[]>([]);
  const [creating, setCreating] = useState(false);

  const loadRows = useCallback(async (opts?: { silent?: boolean }) => {
    if (!workspaceId) {
      setLoading(false);
      setRows([]);
      return;
    }
    if (!opts?.silent) setLoading(true);
    setError(null);

    const { data, error: qErr } = await supabase
      .from('action_items')
      .select(`
        id,
        title,
        status,
        updated_at,
        created_at,
        meeting_id,
        assignee_name,
        assignee_email,
        assignee_user_id,
        meetings!inner (
          id,
          starts_at,
          workspace_id,
          meeting_series ( id, title )
        )
      `)
      .eq('meetings.workspace_id', workspaceId)
      .order('updated_at', { ascending: false });

    if (qErr) {
      setError(qErr.message);
      setRows([]);
    } else {
      setRows((data || []) as Row[]);
    }
    if (!opts?.silent) setLoading(false);
  }, [workspaceId]);

  const loadMembers = useCallback(async () => {
    if (!workspaceId) {
      setMembers([]);
      return;
    }
    const { data: wmRows } = await supabase
      .from('workspace_members')
      .select('user_id')
      .eq('workspace_id', workspaceId)
      .eq('status', 'active');
    const userIds = [...new Set((wmRows as { user_id: string }[] | null || []).map((r) => r.user_id))];
    if (userIds.length === 0) {
      setMembers([]);
      return;
    }
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .in('id', userIds);
    const opts: MemberOption[] = ((profiles as unknown[] | null) || []).map((p) => ({
      user_id: String((p as any).id),
      full_name: ((p as any).full_name as string | null) || null,
      email: ((p as any).email as string | null) || null,
    }));
    opts.sort((a, b) => (a.full_name || a.email || '').localeCompare(b.full_name || b.email || ''));
    setMembers(opts);
  }, [workspaceId]);

  const loadMeetings = useCallback(async () => {
    if (!workspaceId) {
      setMeetings([]);
      return;
    }
    // Only meetings that have actually happened. Action items can only be added to a
    // past occurrence - future ones don't have insights/discussion to attach a task to.
    const nowIso = new Date().toISOString();
    const { data } = await supabase
      .from('meetings')
      .select('id, starts_at, meeting_series ( title )')
      .eq('workspace_id', workspaceId)
      .lte('starts_at', nowIso)
      .order('starts_at', { ascending: false, nullsFirst: false })
      .limit(200);
    const opts: MeetingOption[] = ((data as unknown[] | null) || []).map((m) => ({
      id: String((m as any).id),
      starts_at: ((m as any).starts_at as string | null) || null,
      series_title:
        ((m as any).meeting_series && typeof (m as any).meeting_series === 'object'
          ? (((m as any).meeting_series as { title?: string | null }).title as string | null) || null
          : null),
    }));
    setMeetings(opts);
  }, [workspaceId]);

  useEffect(() => {
    void loadRows();
    void loadMembers();
    void loadMeetings();
  }, [loadRows, loadMembers, loadMeetings]);

  const updateRowTitle = async (id: string, title: string) => {
    const t = title.trim();
    if (!t) return;
    setSavingId(id);
    const { error: uErr } = await supabase
      .from('action_items')
      .update({ title: t, updated_at: new Date().toISOString() })
      .eq('id', id);
    setSavingId(null);
    if (uErr) {
      toast.error(uErr.message);
      return;
    }
    setTitleEditId(null);
    setTitleEditText('');
    await loadRows({ silent: true });
  };

  const deleteRow = async (id: string) => {
    if (!window.confirm('Delete this action item?')) return;
    const { error: dErr } = await supabase.from('action_items').delete().eq('id', id);
    if (dErr) {
      toast.error(dErr.message);
      return;
    }
    await loadRows({ silent: true });
  };

  const resetAddForm = () => {
    setAddTitle('');
    setAddMeetingId('');
    setAddAssigneeUserId(ASSIGNEE_UNASSIGNED);
    setAddMeetingSearch('');
    setAddMeetingPickerOpen(false);
  };

  const createAction = async () => {
    const t = addTitle.trim();
    if (!t) {
      toast.error('Title is required');
      return;
    }
    if (!addMeetingId) {
      toast.error('Pick a meeting');
      return;
    }
    setCreating(true);
    const payload: Record<string, unknown> = {
      meeting_id: addMeetingId,
      title: t,
      status: 'open',
    };
    if (addAssigneeUserId !== ASSIGNEE_UNASSIGNED) {
      payload.assignee_user_id = addAssigneeUserId;
    }
    const { error: cErr } = await supabase.from('action_items').insert(payload);
    setCreating(false);
    if (cErr) {
      toast.error(cErr.message);
      return;
    }
    toast.success('Action item added');
    setAddOpen(false);
    resetAddForm();
    await loadRows({ silent: true });
  };

  const updateRowStatus = async (id: string, status: string) => {
    const prevStatus = rows.find((r) => r.id === id)?.status || null;
    setSavingId(id);
    const { error: uErr } = await supabase
      .from('action_items')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id);
    setSavingId(null);
    if (uErr) {
      toast.error(uErr.message);
      return;
    }
    // Record the change in the follow-through trail (rendered by the nudge/recap emails and the
    // per-item thread). Best-effort - the status change itself already saved.
    if (currentUserId && status !== prevStatus) {
      void supabase.from('action_item_updates').insert({
        action_item_id: id,
        author_user_id: currentUserId,
        author_email: user?.email || null,
        kind: 'status_change',
        old_status: prevStatus,
        new_status: status,
        source: 'app',
      });
      if (status === 'done') track('action_item_completed', { source: 'app' });
    }
    await loadRows({ silent: true });
  };

  const updateAssignee = async (id: string, pick: string) => {
    // Selecting the legacy "external" entry is a no-op - it's just there to surface who the
    // item is currently assigned to when that person isn't a workspace member.
    if (pick.startsWith('__legacy__')) return;
    setSavingId(id);
    const update: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (pick === ASSIGNEE_UNASSIGNED) {
      update.assignee_user_id = null;
      update.assignee_email = null;
      update.assignee_name = null;
    } else {
      // pick is a workspace member user_id.
      update.assignee_user_id = pick;
      update.assignee_email = null;
      update.assignee_name = null;
    }
    const { error: uErr } = await supabase.from('action_items').update(update).eq('id', id);
    setSavingId(null);
    if (uErr) {
      toast.error(uErr.message);
      return;
    }
    await loadRows({ silent: true });
  };

  const memberLabel = (m: MemberOption) => m.full_name?.trim() || m.email?.trim() || `Member (${m.user_id.slice(0, 8)}…)`;

  /**
   * Defensive client-side fallback: if a row was created with assignee_name/assignee_email but no
   * user_id, try matching against workspace members so it doesn't render as "(external)" for a
   * person who is actually a member. The DB-level backfill should usually cover this, but new
   * AI extractions may still arrive without the user_id link.
   */
  const resolveMemberFromLegacy = useCallback(
    (r: Row): MemberOption | null => {
      if (r.assignee_user_id) return null;
      const name = (r.assignee_name || '').trim().toLowerCase();
      const email = (r.assignee_email || '').trim().toLowerCase();
      if (!name && !email) return null;
      return (
        members.find(
          (m) =>
            (name && (m.full_name || '').toLowerCase() === name) ||
            (email && (m.email || '').toLowerCase() === email),
        ) || null
      );
    },
    [members],
  );

  /** Stable key for an assignee, used by the filter combobox. */
  const rowAssigneeKey = useCallback(
    (r: Row): string => {
      const effectiveUserId = r.assignee_user_id || resolveMemberFromLegacy(r)?.user_id || null;
      if (effectiveUserId) return effectiveUserId;
      const ext = (r.assignee_name || r.assignee_email || '').trim().toLowerCase();
      if (ext) return `ext:${ext}`;
      return '__unassigned__';
    },
    [resolveMemberFromLegacy],
  );

  const filteredRows = useMemo(() => {
    const titleQ = titleQuery.trim().toLowerCase();
    return rows.filter((r) => {
      const effectiveUserId = r.assignee_user_id || resolveMemberFromLegacy(r)?.user_id || null;
      if (assignedToMe) {
        if (effectiveUserId !== currentUserId) return false;
      }
      if (statusFilter.size > 0 && !statusFilter.has(normalizeActionStatus(r.status) as any)) return false;
      if (titleQ) {
        const itemTitle = (r.title || '').toLowerCase();
        const seriesTitle = (r.meetings?.meeting_series && typeof r.meetings.meeting_series === 'object'
          ? r.meetings.meeting_series.title || ''
          : ''
        ).toLowerCase();
        if (!itemTitle.includes(titleQ) && !seriesTitle.includes(titleQ)) return false;
      }
      if (assigneeFilter && rowAssigneeKey(r) !== assigneeFilter) return false;
      if (meetingFilter && (r.meetings?.meeting_series?.id ?? '') !== meetingFilter) return false;
      if (ageBucket && !actionAgeInBucket(r.created_at, ageBucket)) return false;
      return true;
    });
  }, [rows, assignedToMe, titleQuery, statusFilter, assigneeFilter, meetingFilter, ageBucket, currentUserId, resolveMemberFromLegacy, rowAssigneeKey]);

  /** Distinct meetings (series) present in the current rows - populates the Meeting filter. */
  const meetingOptions = useMemo(() => {
    const map = new Map<string, { key: string; label: string }>();
    for (const r of rows) {
      const series = r.meetings?.meeting_series;
      const id = series && typeof series === 'object' ? series.id : null;
      if (!id || map.has(id)) continue;
      map.set(id, { key: id, label: series?.title || 'Untitled meeting' });
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  /** Distinct assignees as they appear in the current rows (before filters apply). */
  const assigneeOptions = useMemo(() => {
    const map = new Map<string, { key: string; label: string; subtitle?: string }>();
    for (const r of rows) {
      const key = rowAssigneeKey(r);
      if (map.has(key)) continue;
      if (key === '__unassigned__') {
        map.set(key, { key, label: 'Unassigned' });
      } else if (key.startsWith('ext:')) {
        const display = assigneeDisplay(r) || key.slice(4);
        map.set(key, { key, label: display, subtitle: 'external' });
      } else {
        const m = members.find((m) => m.user_id === key);
        map.set(key, {
          key,
          label: m ? memberLabel(m) : (assigneeDisplay(r) || 'Member'),
          subtitle: m?.email && m.full_name ? m.email : undefined,
        });
      }
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [rows, members, rowAssigneeKey]);

  const toggleStatus = (s: ActionStatus) => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const clearFilters = () => {
    setAssignedToMe(false);
    setAssigneeFilter('');
    setMeetingFilter('');
    setTitleQuery('');
    setStatusFilter(new Set(DEFAULT_STATUS_FILTER));
    setAgeBucket(null);
  };

  const filtersDirty =
    assignedToMe ||
    Boolean(assigneeFilter) ||
    Boolean(meetingFilter) ||
    Boolean(ageBucket) ||
    titleQuery.trim().length > 0 ||
    statusFilter.size !== DEFAULT_STATUS_FILTER.length ||
    DEFAULT_STATUS_FILTER.some((s) => !statusFilter.has(s));

  const selectedAssignee = assigneeFilter
    ? assigneeOptions.find((o) => o.key === assigneeFilter) || null
    : null;

  const selectedMeeting = meetingFilter
    ? meetingOptions.find((o) => o.key === meetingFilter) || null
    : null;

  /**
   * The cleaner runs over whatever the list is currently showing, minus the status filter (a done
   * item has nothing to clean). Everything else - assignee, meeting, search, age - carries over,
   * so hitting Clean up after filtering to one person never touches anyone else's items. This
   * label spells that scope out on the cleaner's start screen.
   */
  const cleanerCandidates = useMemo(
    () => filteredRows.filter((r) => r.status === 'open' || r.status === 'in_progress'),
    [filteredRows],
  );

  const cleanerScopeLabel = useMemo(() => {
    const parts: string[] = [];
    if (assignedToMe) parts.push('assigned to you');
    else if (selectedAssignee) parts.push(`assigned to ${selectedAssignee.label}`);
    if (selectedMeeting) parts.push(`from ${selectedMeeting.label}`);
    if (titleQuery.trim()) parts.push(`matching "${titleQuery.trim()}"`);
    if (ageBucket) parts.push(`(${AGE_BUCKET_LABELS[ageBucket] ?? ageBucket})`);
    if (parts.length === 0) return `in ${workspace?.name || 'this workspace'}`;
    return parts.join(', ');
  }, [assignedToMe, selectedAssignee, selectedMeeting, titleQuery, ageBucket, workspace?.name]);

  // Arrived from the "your backlog is getting long" banner - it already cleared the filters.
  useEffect(() => {
    if (!openCleanerOnMount || loading) return;
    setCleanerOpen(true);
    onConsumedOpenCleaner?.();
  }, [openCleanerOnMount, loading, onConsumedOpenCleaner]);

  return (
    <div className="relative min-h-full pb-3">
      <AppTopBar
        userProfile={userProfile}
        workspaceName={workspace?.name}
        onHelp={onHelp}
        onSignOut={onSignOut}
        onSwitchWorkspace={onSwitchWorkspace}
      />
      <div className="mx-auto max-w-[1400px] space-y-6 px-2 sm:px-4 lg:px-6">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-1">
            <h1 className="font-display text-2xl font-semibold leading-tight tracking-tight text-headline md:text-3xl">
              Action items
            </h1>
            <p className="max-w-xl text-sm text-muted-foreground">
              Tasks captured from your meetings.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCleanerOpen(true)}
              disabled={cleanerCandidates.length === 0}
              className="h-auto rounded-lg px-5 py-4 text-base font-medium"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Clean up
              {cleanerCandidates.length > 0 ? (
                <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
                  {cleanerCandidates.length}
                </span>
              ) : null}
            </Button>
            <Button
              type="button"
              onClick={() => {
                resetAddForm();
                setAddOpen(true);
              }}
              className="h-auto rounded-lg bg-gradient-to-br from-primary to-primary-container px-6 py-4 text-base font-medium text-primary-foreground shadow-lg shadow-primary/20 transition-transform hover:scale-[1.01]"
            >
              <Plus className="mr-2 h-4 w-4" /> Add action item
            </Button>
          </div>
        </header>

        {/* Search + filters bar */}
        <div className="rounded-lg border border-outline-variant/15 bg-card px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                value={titleQuery}
                onChange={(e) => setTitleQuery(e.target.value)}
                placeholder="Search action items or meetings…"
                className="h-10 w-full rounded-lg border-border/60 pl-9 pr-10 text-sm shadow-sm"
              />
              {titleQuery && (
                <button
                  type="button"
                  onClick={() => setTitleQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm sm:ml-auto">
              <input
                type="checkbox"
                checked={assignedToMe}
                onChange={(e) => setAssignedToMe(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-primary"
              />
              <span>Assigned to me</span>
            </label>
            <div className="flex items-center gap-3 rounded-md border border-border/60 bg-background px-3 py-1">
              <span className="text-xs font-medium text-muted-foreground">Status:</span>
              {ACTION_ITEM_STATUSES.map((s) => {
                const active = statusFilter.has(s.value as any);
                return (
                  <label key={s.value} className="inline-flex cursor-pointer items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleStatus(s.value as ActionStatus)}
                      className="h-4 w-4 rounded border-border accent-primary"
                    />
                    <span>{s.label}</span>
                  </label>
                );
              })}
            </div>
            <Popover open={assigneePickerOpen} onOpenChange={setAssigneePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  role="combobox"
                  aria-expanded={assigneePickerOpen}
                  className={cn(
                    'h-8 min-w-[200px] max-w-xs justify-between gap-2 px-3 text-xs font-normal',
                    !selectedAssignee && 'text-muted-foreground',
                  )}
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    <UserCircle className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">
                      {selectedAssignee ? selectedAssignee.label : 'All assignees'}
                    </span>
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder="Search assignees…"
                    className="h-9 border-0 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                  />
                  <CommandList>
                    <CommandEmpty>No assignees found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="__all__ all assignees"
                        onSelect={() => {
                          setAssigneeFilter('');
                          setAssigneePickerOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            !assigneeFilter ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        All assignees
                      </CommandItem>
                      {assigneeOptions.map((opt) => (
                        <CommandItem
                          key={opt.key}
                          value={`${opt.key} ${opt.label} ${opt.subtitle || ''}`}
                          onSelect={() => {
                            setAssigneeFilter(opt.key);
                            setAssigneePickerOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              assigneeFilter === opt.key ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          <span className="flex min-w-0 flex-col">
                            <span className="truncate">{opt.label}</span>
                            {opt.subtitle ? (
                              <span className="truncate text-[10px] text-muted-foreground">
                                {opt.subtitle}
                              </span>
                            ) : null}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Popover open={meetingPickerOpen} onOpenChange={setMeetingPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  role="combobox"
                  aria-expanded={meetingPickerOpen}
                  className={cn(
                    'h-8 min-w-[200px] max-w-xs justify-between gap-2 px-3 text-xs font-normal',
                    !selectedMeeting && 'text-muted-foreground',
                  )}
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">
                      {selectedMeeting ? selectedMeeting.label : 'All meetings'}
                    </span>
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder="Search meetings…"
                    className="h-9 border-0 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                  />
                  <CommandList>
                    <CommandEmpty>No meetings found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="__all__ all meetings"
                        onSelect={() => {
                          setMeetingFilter('');
                          setMeetingPickerOpen(false);
                        }}
                      >
                        <Check className={cn('mr-2 h-4 w-4', !meetingFilter ? 'opacity-100' : 'opacity-0')} />
                        All meetings
                      </CommandItem>
                      {meetingOptions.map((opt) => (
                        <CommandItem
                          key={opt.key}
                          value={`${opt.key} ${opt.label}`}
                          onSelect={() => {
                            setMeetingFilter(opt.key);
                            setMeetingPickerOpen(false);
                          }}
                        >
                          <Check
                            className={cn('mr-2 h-4 w-4', meetingFilter === opt.key ? 'opacity-100' : 'opacity-0')}
                          />
                          <span className="truncate">{opt.label}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {ageBucket && (
              <button
                type="button"
                onClick={() => setAgeBucket(null)}
                className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/15"
              >
                {AGE_BUCKET_LABELS[ageBucket] ?? ageBucket}
                <X className="h-3 w-3" />
              </button>
            )}
            {filtersDirty && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={clearFilters}
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              >
                <X className="mr-1 h-3 w-3" />
                Clear
              </Button>
            )}
            <span className="text-xs text-muted-foreground">
              {filteredRows.length} of {rows.length}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
            <p className="text-sm text-muted-foreground">Loading action items…</p>
          </div>
        ) : error ? (
          <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </p>
        ) : (
          <section className="rounded-2xl border border-outline-variant/15 bg-card shadow-sm">
            {filteredRows.length === 0 ? (
              <p className="px-6 py-16 text-center text-sm text-muted-foreground">
                {rows.length === 0
                  ? 'No action items yet.'
                  : 'No action items match the current filters.'}
              </p>
            ) : (
              <ul className="divide-y divide-outline-variant/10">
                {filteredRows.map((r) => {
                  const sb = statusBadgeClass(r.status);
                  const seriesTitle =
                    r.meetings?.meeting_series && typeof r.meetings.meeting_series === 'object'
                      ? r.meetings.meeting_series.title
                      : null;
                  const resolvedMember = resolveMemberFromLegacy(r);
                  const effectiveUserId = r.assignee_user_id || resolvedMember?.user_id || null;
                  const fallbackName = assigneeDisplay(r);
                  // Truly external = no user_id AND no member-name match
                  const hasExternalAssignee = !effectiveUserId && Boolean(fallbackName);
                  const assigneePick = effectiveUserId
                    || (hasExternalAssignee ? `__legacy__${r.id}` : ASSIGNEE_UNASSIGNED);
                  const isDone = r.status === 'done';
                  return (
                    <li key={r.id} className={cn('px-5 py-3', isDone && 'opacity-70')}>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0 flex-1">
                        {titleEditId === r.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              className="h-8 text-sm"
                              autoFocus
                              value={titleEditText}
                              onChange={(e) => setTitleEditText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  void updateRowTitle(r.id, titleEditText);
                                } else if (e.key === 'Escape') {
                                  setTitleEditId(null);
                                  setTitleEditText('');
                                }
                              }}
                            />
                            <Button
                              type="button"
                              size="sm"
                              className="h-8"
                              onClick={() => void updateRowTitle(r.id, titleEditText)}
                            >
                              Save
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-8"
                              onClick={() => {
                                setTitleEditId(null);
                                setTitleEditText('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <div className="group flex items-center gap-2">
                            <p className={cn('min-w-0 flex-1 font-medium text-foreground', isDone && 'line-through text-muted-foreground')}>
                              {r.title}
                            </p>
                            <button
                              type="button"
                              className="opacity-0 transition-opacity group-hover:opacity-100"
                              onClick={() => {
                                setTitleEditId(r.id);
                                setTitleEditText(r.title || '');
                              }}
                              aria-label="Edit action item"
                            >
                              <Pencil className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                            </button>
                          </div>
                        )}
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {seriesTitle || 'Meeting'} · {formatSessionDate(r.meetings?.starts_at ?? null)}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <Select
                          value={assigneePick}
                          disabled={savingId === r.id}
                          onValueChange={(v) => void updateAssignee(r.id, v)}
                        >
                          <SelectTrigger className="h-8 w-[200px] text-xs">
                            <SelectValue placeholder="Unassigned" />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            <SelectItem value={ASSIGNEE_UNASSIGNED}>Unassigned</SelectItem>
                            {members.map((m) => (
                              <SelectItem key={m.user_id} value={m.user_id}>
                                {memberLabel(m)}
                              </SelectItem>
                            ))}
                            {/* Surface the raw assignee_name for items already assigned to someone
                                who isn't a workspace member. Selecting it is a no-op (keeps the
                                current external assignee) - picking a member or Unassigned overwrites. */}
                            {hasExternalAssignee ? (
                              <SelectItem value={`__legacy__${r.id}`}>
                                {fallbackName} (external)
                              </SelectItem>
                            ) : null}
                          </SelectContent>
                        </Select>
                        <Select
                          value={normalizeActionStatus(r.status)}
                          disabled={savingId === r.id}
                          onValueChange={(next) => void updateRowStatus(r.id, next)}
                        >
                          <SelectTrigger className={cn('h-8 w-[128px] text-xs border', sb)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ACTION_ITEM_STATUSES.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {savingId === r.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden />
                        ) : null}
                        <button
                          type="button"
                          className={cn(
                            'rounded p-1 transition-colors hover:bg-muted',
                            threadOpenId === r.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                          )}
                          onClick={() => setThreadOpenId(threadOpenId === r.id ? null : r.id)}
                          aria-label="Updates and comments"
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          className="rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => void deleteRow(r.id)}
                          aria-label="Delete action item"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      </div>
                      {threadOpenId === r.id ? (
                        <ActionItemThread
                          actionItemId={r.id}
                          currentUserId={currentUserId}
                          currentUserEmail={user?.email || null}
                        />
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </div>

      <ActionItemCleaner
        open={cleanerOpen}
        onOpenChange={setCleanerOpen}
        items={cleanerCandidates}
        scopeLabel={cleanerScopeLabel}
        currentUserId={currentUserId}
        currentUserEmail={user?.email || null}
        onFinished={(changed) => {
          if (changed) void loadRows({ silent: true });
        }}
      />

      {/* Add action item dialog */}
      <Dialog
        open={addOpen}
        onOpenChange={(o) => {
          setAddOpen(o);
          if (!o) resetAddForm();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add action item</DialogTitle>
            <DialogDescription>Attach a new task to one of your meetings.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="add-title">Title</Label>
              <Input
                id="add-title"
                placeholder="What needs to be done?"
                value={addTitle}
                onChange={(e) => setAddTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && addMeetingId && addTitle.trim()) {
                    e.preventDefault();
                    void createAction();
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Meeting</Label>
              <Popover open={addMeetingPickerOpen} onOpenChange={setAddMeetingPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={addMeetingPickerOpen}
                    className={cn(
                      'w-full justify-between text-sm font-normal',
                      !addMeetingId && 'text-muted-foreground',
                    )}
                  >
                    <span className="truncate">
                      {addMeetingId
                        ? (() => {
                            const m = meetings.find((x) => x.id === addMeetingId);
                            if (!m) return 'Meeting selected';
                            return `${m.series_title || 'Meeting'} · ${formatSessionDate(m.starts_at)}`;
                          })()
                        : 'Pick a meeting'}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[360px] p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Search meetings…"
                      value={addMeetingSearch}
                      onValueChange={setAddMeetingSearch}
                      className="h-9 border-0 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                    />
                    <CommandList>
                      <CommandEmpty>No meetings found.</CommandEmpty>
                      <CommandGroup>
                        {meetings.map((m) => {
                          const label = `${m.series_title || 'Meeting'} · ${formatSessionDate(m.starts_at)}`;
                          return (
                            <CommandItem
                              key={m.id}
                              value={`${m.id} ${label}`}
                              onSelect={() => {
                                setAddMeetingId(m.id);
                                setAddMeetingPickerOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  addMeetingId === m.id ? 'opacity-100' : 'opacity-0',
                                )}
                              />
                              <span className="truncate">{label}</span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-assignee">Assignee</Label>
              <Select value={addAssigneeUserId} onValueChange={setAddAssigneeUserId}>
                <SelectTrigger id="add-assignee" className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value={ASSIGNEE_UNASSIGNED}>Unassigned</SelectItem>
                  {members.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {memberLabel(m)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setAddOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void createAction()}
              disabled={creating || !addTitle.trim() || !addMeetingId}
            >
              {creating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
